import React, { useEffect, useRef, useState } from 'react';
import validator from 'validator';
import loginLogo from '../../assets/gateway-logo-navy.svg';
import splash from '../../assets/gateway-login-left.svg';
import splash2 from '../../assets/gateway-login-right.svg';
import {
    Box,
    Button,
    LinearProgress,
    Link,
    TextField,
    Typography
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    useCheckSSOMutation,
    useForgotPasswordMutation,
    useLoginMutation,
} from "../../services/grace";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useAlertDispatch } from '../../context/AlertContext/AlertContext';

enum PageModes {
    Email = "email",
    Password = "password",
    ForgotPassword = "ForgotPassword",
}

declare type PageMode = PageModes.Email | PageModes.Password | PageModes.ForgotPassword;

interface FieldError {
    isError: boolean;
    errorText: string;
}

export default function Login(): JSX.Element {
    const alertDispatch = useAlertDispatch();

    const [pageMode, setPageMode] = useState<PageMode>(PageModes.Email);
    const [inProgress, setInProgress] = useState(false);
    const [title, setTitle] = useState<string>("Sign in");
    const [signInDisabled, setSignInDisabled] = useState<boolean>(true);

    const emailRef = useRef<HTMLInputElement>();
    const passwordRef = useRef<HTMLInputElement>();


    const [email, setEmail] = useState("");
    const [emailFieldError, setEmailFieldError] = useState<FieldError>({
        isError: false,
        errorText: ""
    });

    const [password, setPassword] = useState("");
    const [passwordFieldError, setPasswordFieldError] = useState<FieldError>({
        isError: false,
        errorText: ""
    });

    const [checkSSO] = useCheckSSOMutation();
    const [login] = useLoginMutation();

    // Update the title and focus the appropriate
    // field when the page mode changes
    useEffect(() => {
        if (pageMode === PageModes.Email) {
            setTitle("Sign in");
            setTimeout(() => {
                emailRef?.current?.focus();
            }, 50);
        } else if (pageMode === PageModes.ForgotPassword) {
            setTitle("Forgot your password?");
        } else {
            setTitle(`Signing in as ${email}`);
            setTimeout(() => {
                passwordRef?.current?.focus();
            }, 50);
        }
    }, [pageMode, email])

    // Update the error messages and disabled the
    // sign-in button as required
    useEffect(() => {
        if (pageMode === PageModes.Email) {
            if (email.trim().length === 0 || validator.isEmail(email)) {
                setEmailFieldError({
                    isError: false,
                    errorText: "",
                })

                setSignInDisabled(email.trim().length === 0);
            } else {
                setEmailFieldError({
                    isError: true,
                    errorText: "Invalid email address specified",
                });
                setSignInDisabled(true);
            }
        } else {
            if (password.trim().length === 0) {
                setPasswordFieldError({
                    isError: false,
                    errorText: "",
                })

                setSignInDisabled(password.trim().length === 0);
            } else {
                setSignInDisabled(false);
            }
        }
    }, [pageMode, email, password])

    const submit = async () => {
        if (pageMode === PageModes.Email) {
            if (!validator.isEmail(email)) {
                setEmailFieldError({
                    isError: true,
                    errorText: "Invalid email address specified",
                });
                return;
            }

            try {
                setInProgress(true);
                const res = await checkSSO(email).unwrap();
                if (!res.success) {
                    setPageMode(PageModes.Password);
                } else {
                    alertDispatch({ type: 'redirect', payload: `/api/user/login/${res.message}` })
                }
            } catch (error) {
                const err = error as FetchBaseQueryError;
                console.error("Failed to check SSO status");
                console.error(err);

                alertDispatch({
                    type: 'add',
                    payload: {
                        type: "auth",
                        message: "Unable to check SSO status",
                    }
                });
            }

            setInProgress(false);
        } else if (pageMode === PageModes.Password) {
            if (password.trim().length === 0) {
                setPasswordFieldError({
                    isError: true,
                    errorText: "No password specified",
                })
                return;
            }
            setInProgress(true);

            try {
                setInProgress(true);
                await login({ email, password }).unwrap();
                alertDispatch({ type: 'redirect', payload: '/' });
            } catch (error) {
                const err = error as FetchBaseQueryError;
                console.error("Failed to login!");
                console.error(err);

                setPasswordFieldError({
                    isError: true,
                    errorText: "Incorrect password or user. Try password again or Change user",
                });

                setTimeout(() => {
                    passwordRef?.current?.focus();
                }, 50);
            }

            setInProgress(false);
        }
    };

    const handleKeyDown = (evt: React.KeyboardEvent<EventTarget & HTMLInputElement>) => {
        if (evt.code === 'Enter') {
            submit();
        }
    };

    const handleEmailChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(evt.target.value);
        if (!validator.isEmail(evt.target.value)) {
            setEmailFieldError({
                isError: true,
                errorText: "Invalid email address specified",
            });
        } else {
            setEmailFieldError({ isError: false, errorText: "" });
        }
    };

    const handlePasswordChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setPassword(evt.target.value);
    }

    const handleSignInClick = () => {
        submit();
    };

    const handleChangeUserClick = () => {
        setPassword("");
        setPasswordFieldError({
            isError: false,
            errorText: "",
        })
        setPageMode(PageModes.Email);
    };

    const [forgotPassword] = useForgotPasswordMutation();
    const handleForgotPasswordClick = async () => {
        try {
            setInProgress(true);
            await forgotPassword(email).unwrap();
            setPageMode(PageModes.ForgotPassword);
        } catch (error) {
            const err = error as FetchBaseQueryError;
            console.error("Failed to trigger forgot password email");
            console.error(err);

            alertDispatch({
                type: 'add',
                payload: {
                    type: "auth",
                    message: "Unable to reset password",
                }
            });
        }
        setInProgress(false);
    }

    const handleBackToSignInLink = () => setPageMode(PageModes.Email);

    return (
        <Box sx={{
            display: "flex",
            flexWrap: "nowrap",
            alignItems: "center",
            minHeight: "100vh",
            width: "100%"
        }}>
            <Box component="img" src={splash} alt="Splash" sx={{
                flex: 1,
                width: "100%",
                maxWidth: "774px",
                minWidth: "250px",
                alignSelf: "flex-start"
            }} />
            <Box sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: 1
            }}>
                <img src={loginLogo} alt="Logo" width="280px"/>
                <Typography variant="h5">
                    {title}
                </Typography>

                {pageMode === PageModes.Email && (
                    <TextField
                        inputRef={emailRef}
                        sx={{ marginBottom: 1 }}
                        variant="standard"
                        disabled={inProgress}
                        required
                        error={emailFieldError.isError}
                        helperText={emailFieldError.errorText}
                        fullWidth
                        id="email"
                        label="Email address"
                        name="email"
                        autoComplete="email"
                        onKeyDown={handleKeyDown}
                        onChange={handleEmailChange}
                        value={email}
                        autoFocus
                    />
                )}

                {pageMode === PageModes.Password && (
                    <TextField
                        inputRef={passwordRef}
                        sx={{ marginBottom: 1 }}
                        variant="standard"
                        disabled={inProgress}
                        required
                        error={passwordFieldError.isError}
                        helperText={passwordFieldError.errorText}
                        fullWidth
                        id="password"
                        label="Password"
                        name="password"
                        onKeyDown={handleKeyDown}
                        onChange={handlePasswordChange}
                        value={password}
                        type="password"
                        autoFocus
                    />
                )}

                {(pageMode === PageModes.Email || pageMode === PageModes.Password) && (
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        sx={{ marginBottom: 1 }}
                        disabled={signInDisabled || inProgress}
                        onClick={handleSignInClick}
                    >
                        Sign In
                    </Button>
                )}
 
                {pageMode === PageModes.Password && (
                    <>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{ marginBottom: 1 }}
                            disabled={inProgress}
                            onClick={handleChangeUserClick}
                        >
                            Change user
                        </Button>

                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            sx={{ marginBottom: 1 }}
                            disabled={inProgress}
                            onClick={handleForgotPasswordClick}
                        >
                            Forgot Password
                        </Button>
                    </>
                )}

                {pageMode === PageModes.ForgotPassword && 
                    <Box sx={{ margin: 3 }}>
                        <p>If the email address you provided is correct, we will send an email with a link to reset your password.</p>
                        <p>If you don't receive a link, check that you are using the correct email address.</p>
                        <p>Please contact your account representative for further assistance.</p>
                        <div>
                            
                            <Link onClick={handleBackToSignInLink} color="primary" underline="none" sx={{ cursor: "pointer" }}>
                                <Typography variant="h5">
                                    Back to Sign in 
                                    <FontAwesomeIcon 
                                        style={{ marginLeft: "10px" }} 
                                        icon={['fas', 'arrow-right']} /> 
                                </Typography>
                            </Link>
                        </div>
                    </Box>
                }

                {inProgress && <LinearProgress color="secondary" />}
            </Box>
            <Box component="img" src={splash2} alt="Splash" sx={{
                flex: 1,
                width: "100%",
                maxWidth: "774px",
                minWidth: "250px",
                alignSelf: "flex-end"
            }} />
        </Box>
    );
}
